import { acceptHMRUpdate, defineStore } from 'pinia'

import { Breakpoints } from '~/enums/breakpoints'

export type MiscResizeState = Pick<
  ReturnType<typeof useMiscResizeStore>,
  'screenWidth' | 'screenHeight' | 'hasEventListener'
>

export const useMiscResizeStore = defineStore('miscResize', () => {
  const { isMobile, isTablet } = useDevice()

  const screenWidth = ref(
    isMobile ? Breakpoints.sm : isTablet ? Breakpoints.md : Breakpoints.lg,
  )
  const screenHeight = ref(720)
  const hasEventListener = ref(false)

  function SET_SCREEN_SIZE({ sizeX, sizeY }: { sizeX: number; sizeY: number }) {
    screenHeight.value = sizeY
    screenWidth.value = sizeX
  }

  function SET_HAS_EVENT_LISTENER() {
    hasEventListener.value = true
  }

  function SETUP_EVENT_LISTENERS() {
    if (import.meta.client && !hasEventListener.value) {
      window.addEventListener('resize', () => {
        COMPUTE_AND_SET_SCREEN_SIZE()
      })
      COMPUTE_AND_SET_SCREEN_SIZE()
    }
  }

  function COMPUTE_AND_SET_SCREEN_SIZE() {
    if (
      screenHeight.value !== window.innerHeight ||
      screenWidth.value !== window.innerWidth
    ) {
      SET_SCREEN_SIZE({
        sizeX: window.innerWidth,
        sizeY: window.innerHeight,
      })
    }

    if (!hasEventListener.value) SET_HAS_EVENT_LISTENER()
  }

  const SCREEN_WIDTH = computed(() => {
    return screenWidth.value
  })

  const SCREEN_HEIGHT = computed(() => {
    return screenHeight.value
  })

  return {
    // state
    screenWidth,
    screenHeight,
    hasEventListener,

    // actions
    SET_SCREEN_SIZE,
    SET_HAS_EVENT_LISTENER,
    SETUP_EVENT_LISTENERS,
    COMPUTE_AND_SET_SCREEN_SIZE,

    // getters
    SCREEN_WIDTH,
    SCREEN_HEIGHT,
  }
})

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useMiscResizeStore, import.meta.hot))
